import React, { useState, useEffect } from "react";
import { emailAccountsAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from "../components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Mail,
  Plus,
  Trash2,
  Loader2,
  RefreshCw,
  Server,
  Lock,
  CheckCircle,
  AlertCircle,
  Edit,
} from "lucide-react";

export function EmailAccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [showSheet, setShowSheet] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [message, setMessage] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    imap_server: "",
    imap_port: 993,
    username: "",
    password: "",
    use_ssl: true,
    account_type: "pec",
  });

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const response = await emailAccountsAPI.getAll();
      setAccounts(response.data);
    } catch (error) {
      showMessage("Errore nel caricamento degli account", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.imap_server || !formData.username) {
      showMessage("Compila tutti i campi obbligatori", "error");
      return;
    }
    if (!editingAccount && !formData.password) {
      showMessage("La password e obbligatoria per i nuovi account", "error");
      return;
    }

    setSaving(true);
    try {
      if (editingAccount) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await emailAccountsAPI.update(editingAccount.id, updateData);
        showMessage("Account aggiornato");
      } else {
        await emailAccountsAPI.create(formData);
        showMessage("Account email configurato");
      }
      setShowSheet(false);
      setEditingAccount(null);
      resetForm();
      loadAccounts();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore durante il salvataggio", "error");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      imap_server: "",
      imap_port: 993,
      username: "",
      password: "",
      use_ssl: true,
      account_type: "pec",
    });
  };

  const openEditSheet = (account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      email: account.email,
      imap_server: account.imap_server,
      imap_port: account.imap_port,
      username: account.username,
      password: "",
      use_ssl: account.use_ssl,
      account_type: account.account_type,
    });
    setShowSheet(true);
  };

  const openNewSheet = () => {
    setEditingAccount(null);
    resetForm();
    setShowSheet(true);
  };

  const handleSync = async (accountId) => {
    setSyncing(accountId);
    try {
      const response = await emailAccountsAPI.sync(accountId);
      showMessage(response.data.message);
      loadAccounts();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore durante la sincronizzazione", "error");
    } finally {
      setSyncing(null);
    }
  };

  const handleDeleteClick = (accountId) => {
    setDeleteId(accountId);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await emailAccountsAPI.delete(deleteId);
      showMessage("Account eliminato");
      loadAccounts();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore durante l'eliminazione", "error");
    } finally {
      setDeleteId(null);
      setShowDeleteDialog(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Mai sincronizzato";
    const date = new Date(dateString);
    return date.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Preset configurations for common email providers
  const presets = [
    { name: "Aruba PEC", imap_server: "imaps.pec.aruba.it", imap_port: 993 },
    { name: "Legalmail PEC", imap_server: "mbox.cert.legalmail.it", imap_port: 993 },
    { name: "Postecert PEC", imap_server: "mail.postecert.it", imap_port: 993 },
    { name: "Gmail", imap_server: "imap.gmail.com", imap_port: 993 },
    { name: "Outlook/Office365", imap_server: "outlook.office365.com", imap_port: 993 },
  ];

  const applyPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      imap_server: preset.imap_server,
      imap_port: preset.imap_port,
      account_type: preset.name.includes("PEC") ? "pec" : "email",
    }));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Message Banner */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          message.type === "error" 
            ? "bg-red-500 text-white" 
            : "bg-emerald-500 text-white"
        }`}>
          {message.text}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma eliminazione</DialogTitle>
            <DialogDescription>
              Eliminare questo account? Le email nella coda di revisione verranno eliminate.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-email-account">
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
            <Mail size={32} className="text-[#2D7BB7]" />
            Account Email
          </h1>
          <p className="text-muted-foreground mt-1">
            Configura gli account PEC ed email per importare automaticamente
          </p>
        </div>
        <Button
          onClick={openNewSheet}
          className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white shadow-md"
          data-testid="add-account-btn"
        >
          <Plus className="mr-2" size={18} />
          Aggiungi Account
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Come funziona:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Configura un account email (PEC o tradizionale)</li>
                <li>Clicca "Sincronizza" per scaricare le nuove email</li>
                <li>Le email appariranno nella "Coda Email" per la revisione</li>
                <li>Decidi quali protocollare, ignorare o segnare come spam</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accounts List */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
            <Server size={20} className="text-[#2D7BB7]" />
            Account Configurati ({accounts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[...Array(2)].map((_, i) => (
                <Skeleton key={i} className="h-24 bg-muted rounded-lg" />
              ))}
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Mail size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nessun account configurato</p>
              <p className="text-sm mt-2">Aggiungi un account PEC o email per iniziare</p>
            </div>
          ) : (
            <div className="space-y-4">
              {accounts.map((account) => (
                <div
                  key={account.id}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border"
                  data-testid={`account-item-${account.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      account.account_type === "pec" ? "bg-amber-100" : "bg-blue-100"
                    }`}>
                      <Mail size={24} className={
                        account.account_type === "pec" ? "text-amber-600" : "text-blue-600"
                      } />
                    </div>
                    <div>
                      <p className="text-foreground font-medium flex items-center gap-2">
                        {account.name}
                        <Badge className={
                          account.account_type === "pec" 
                            ? "bg-amber-100 text-amber-800 border-amber-200" 
                            : "bg-blue-100 text-blue-800 border-blue-200"
                        }>
                          {account.account_type.toUpperCase()}
                        </Badge>
                        {account.is_active ? (
                          <CheckCircle size={16} className="text-emerald-500" />
                        ) : (
                          <AlertCircle size={16} className="text-red-500" />
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">{account.email}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Ultima sincronizzazione: {formatDate(account.last_sync)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditSheet(account)}
                      className="border-border text-foreground hover:bg-muted"
                      data-testid={`edit-account-${account.id}`}
                    >
                      <Edit className="mr-1" size={14} />
                      Modifica
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSync(account.id)}
                      disabled={syncing === account.id}
                      className="border-border text-foreground"
                      data-testid={`sync-account-${account.id}`}
                    >
                      {syncing === account.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw size={16} className="mr-1" />
                      )}
                      Sincronizza
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteClick(account.id)}
                      className="text-muted-foreground hover:text-red-600 hover:bg-red-50"
                      data-testid={`delete-account-${account.id}`}
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Sheet */}
      <Sheet open={showSheet} onOpenChange={(open) => {
        setShowSheet(open);
        if (!open) setEditingAccount(null);
      }}>
        <SheetContent className="bg-card border-border overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-foreground">
              {editingAccount ? "Modifica Account Email" : "Nuovo Account Email"}
            </SheetTitle>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-6">
            {/* Presets */}
            <div className="space-y-2">
              <Label className="text-foreground">Configurazione rapida</Label>
              <div className="flex flex-wrap gap-2">
                {presets.map((preset) => (
                  <Button
                    key={preset.name}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => applyPreset(preset)}
                    className="text-xs"
                  >
                    {preset.name}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Nome account *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Es: PEC Aziendale"
                className="bg-background border-input text-foreground"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="pec@splendorsud.it"
                className="bg-background border-input text-foreground"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Tipo account</Label>
              <Select
                value={formData.account_type}
                onValueChange={(val) => setFormData({ ...formData, account_type: val })}
              >
                <SelectTrigger className="bg-background border-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pec">PEC (Posta Certificata)</SelectItem>
                  <SelectItem value="email">Email tradizionale</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-foreground">Server IMAP *</Label>
                <Input
                  value={formData.imap_server}
                  onChange={(e) => setFormData({ ...formData, imap_server: e.target.value })}
                  placeholder="imaps.pec.aruba.it"
                  className="bg-background border-input text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Porta</Label>
                <Input
                  type="number"
                  value={formData.imap_port}
                  onChange={(e) => setFormData({ ...formData, imap_port: parseInt(e.target.value) })}
                  className="bg-background border-input text-foreground"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Username *</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="pec@splendorsud.it"
                className="bg-background border-input text-foreground"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-foreground flex items-center gap-2">
                <Lock size={14} />
                Password {editingAccount ? "(lascia vuoto per non modificare)" : "*"}
              </Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={editingAccount ? "Lascia vuoto per non modificare" : "••••••••"}
                className="bg-background border-input text-foreground"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-foreground">Usa SSL/TLS</Label>
              <Switch
                checked={formData.use_ssl}
                onCheckedChange={(checked) => setFormData({ ...formData, use_ssl: checked })}
              />
            </div>

            <SheetFooter className="mt-6">
              <SheetClose asChild>
                <Button type="button" variant="outline" className="border-border text-foreground">
                  Annulla
                </Button>
              </SheetClose>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingAccount ? "Salva Modifiche" : "Salva Account"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
